import React, { useMemo, useState } from 'react';
import { Button, Card, Input, Modal, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { parseOrderIdsFromText } from './utils/parseOrderIds';
import {
    MANUAL_ENTER_ORDER_POOL,
    ManualEnterOrderPoolResponse,
    ManualEnterOrderPoolVariables
} from './graphql/mutations/manualEnterOrderPool';
import {
    MANUAL_EXIT_ORDER_POOL,
    ManualExitOrderPoolResponse,
    ManualExitOrderPoolVariables
} from './graphql/mutations/manualExitOrderPool';

const { Title, Text } = Typography;
const { TextArea } = Input;

type Mode = 'enter' | 'exit';

const ManualOrderPoolPage: React.FC = () => {
    const { t } = useTranslation();
    const [rawText, setRawText] = useState<string>('');

    const parsed = useMemo(() => parseOrderIdsFromText(rawText), [rawText]);

    const [manualEnter, { loading: enterLoading }] = useMutation<
        ManualEnterOrderPoolResponse,
        ManualEnterOrderPoolVariables
    >(MANUAL_ENTER_ORDER_POOL);
    const [manualExit, { loading: exitLoading }] = useMutation<
        ManualExitOrderPoolResponse,
        ManualExitOrderPoolVariables
    >(MANUAL_EXIT_ORDER_POOL);

    const loading = enterLoading || exitLoading;

    const confirmAndSubmit = async (mode: Mode) => {
        const result = parseOrderIdsFromText(rawText);
        if (!result.ok) {
            message.error(t(result.errorMessageKey));
            return;
        }

        const orderIds = result.orderIds;
        const title =
            mode === 'enter'
                ? t('supportTools.orderPool.confirm.enterTitle')
                : t('supportTools.orderPool.confirm.exitTitle');
        const okText =
            mode === 'enter'
                ? t('supportTools.orderPool.buttons.enter')
                : t('supportTools.orderPool.buttons.exit');
        const content =
            mode === 'enter'
                ? t('supportTools.orderPool.confirm.enterContent')
                : t('supportTools.orderPool.confirm.exitContent');

        Modal.confirm({
            title,
            okText,
            cancelText: t('common.cancel'),
            okButtonProps: { danger: true, loading },
            content: (
                <div className="space-y-2">
                    <Text type="danger" className="text-base font-semibold">
                        {content}
                    </Text>
                    <div className="text-sm text-gray-500">
                        {t('supportTools.orderPool.confirm.orderCount', { count: orderIds.length })}
                    </div>
                </div>
            ),
            onOk: async () => {
                try {
                    if (mode === 'enter') {
                        await manualEnter({ variables: { orderIds } });
                        message.success(t('supportTools.orderPool.messages.enterSuccess'));
                    } else {
                        await manualExit({ variables: { orderIds } });
                        message.success(t('supportTools.orderPool.messages.exitSuccess'));
                    }
                } catch (e: any) {
                    message.error(e?.message ?? t('common.operationFailed'));
                    throw e;
                }
            }
        });
    };

    return (
        <div className="h-full w-full flex flex-col gap-4">
            <div className="rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-8 text-white shadow-sm">
                <Title level={2} style={{ margin: 0, color: 'white' }}>
                    {t('supportTools.orderPool.title')}
                </Title>
                <div className="mt-2 text-white/90 text-base">
                    {t('supportTools.orderPool.subtitle')}
                </div>
            </div>

            <Card className="rounded-2xl shadow-sm">
                <div className="flex flex-col gap-3">
                    <Text className="text-base font-medium">
                        {t('supportTools.orderPool.input.label')}
                    </Text>
                    <TextArea
                        value={rawText}
                        onChange={(e) => setRawText(e.target.value)}
                        placeholder={t('supportTools.orderPool.input.placeholder') ?? ''}
                        autoSize={{ minRows: 6, maxRows: 12 }}
                        className="text-base"
                    />
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                            {parsed.ok
                                ? t('supportTools.orderPool.input.parsedCount', { count: parsed.orderIds.length })
                                : t('supportTools.orderPool.input.helper')}
                        </div>
                        <div className="flex gap-3">
                            <Button
                                type="primary"
                                size="large"
                                disabled={!parsed.ok || loading}
                                onClick={() => confirmAndSubmit('enter')}
                            >
                                {t('supportTools.orderPool.buttons.enter')}
                            </Button>
                            <Button
                                danger
                                type="primary"
                                size="large"
                                disabled={!parsed.ok || loading}
                                onClick={() => confirmAndSubmit('exit')}
                            >
                                {t('supportTools.orderPool.buttons.exit')}
                            </Button>
                        </div>
                    </div>
                    {!parsed.ok && rawText.trim() ? (
                        <Text type="danger">{t(parsed.errorMessageKey)}</Text>
                    ) : null}
                </div>
            </Card>
        </div>
    );
};

export default ManualOrderPoolPage;
